/**
 * ForecastQuality — Forecast Quality Score Dashboard
 *
 * KPI-Cards, Kunden-Ranking, Detail-Expansion mit Tages-Chart.
 * Liest Scores aus GET /api/forecast/quality und /api/forecast/quality/summary.
 *
 * @page /forecast/quality
 * @roles admin, manager
 * @since v2.45.0
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { NavLink } from 'react-router-dom';
import { ChevronLeft, ChevronRight, ChevronDown, ChevronUp, TrendingUp, TrendingDown, Minus, Target, RefreshCw, Crosshair, GitCompareArrows, Gauge } from 'lucide-react';
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Button } from '../components/ui';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import SharedForecastTabs from '../components/SharedForecastTabs';

const API = '';

function api(path) {
  return fetch(path, { credentials: 'include' }).then(r => r.ok ? r.json() : Promise.reject(r));
}

// Safe date parsing — handles YYYY-MM-DD without timezone issues
function safeDate(d) {
  if (!d) return null;
  return new Date(d.length === 10 ? d + 'T12:00:00' : d);
}

function fmtDate(d, locale, opts) {
  const dt = safeDate(d);
  if (!dt || isNaN(dt.getTime())) return '–';
  return dt.toLocaleDateString(locale === 'de' ? 'de-DE' : 'en-US', opts);
}

// Simple markdown parser for AI report text
function renderMarkdown(text) {
  if (!text) return '';
  return text
    .replace(/^### (.*$)/gm, '<h4 class="fq-md-h4">$1</h4>')
    .replace(/^## (.*$)/gm, '<h3 class="fq-md-h3">$1</h3>')
    .replace(/^# (.*$)/gm, '<h2 class="fq-md-h2">$1</h2>')
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/^\d+\.\s+(.*$)/gm, '<div class="fq-md-li">$1</div>')
    .replace(/^[-•]\s+(.*$)/gm, '<div class="fq-md-li">$1</div>')
    .replace(/\n\n/g, '<br/>')
    .replace(/\n/g, '<br/>');
}

// Score color based on thresholds
function scoreColor(score, config) {
  if (score === null || score === undefined) return '#9CA3AF';
  const ex = config?.thresholds?.excellent || 90;
  const good = config?.thresholds?.good || 70;
  const mod = config?.thresholds?.moderate || 50;
  if (score >= ex) return '#10B981';
  if (score >= good) return '#338edc';
  if (score >= mod) return '#F59E0B';
  return '#EF4444';
}

function scoreLabel(score, config, t) {
  if (score === null || score === undefined) return '–';
  const ex = config?.thresholds?.excellent || 90;
  const good = config?.thresholds?.good || 70;
  const mod = config?.thresholds?.moderate || 50;
  if (score >= ex) return t('forecastQuality.excellent');
  if (score >= good) return t('forecastQuality.good');
  if (score >= mod) return t('forecastQuality.moderate');
  return t('forecastQuality.poor');
}

function biasColor(bias) {
  if (bias === null || bias === undefined) return '#9CA3AF';
  const abs = Math.abs(bias);
  if (abs <= 10) return '#10B981';
  if (abs <= 30) return '#F59E0B';
  return '#EF4444';
}

// SVG Score Ring
function ScoreRing({ score, size = 60, strokeWidth = 5 }) {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const pct = score !== null && score !== undefined ? Math.min(100, Math.max(0, score)) / 100 : 0;
  const offset = circumference * (1 - pct);
  const color = scoreColor(score, null);
  return (
    <svg width={size} height={size} className="flex-shrink-0">
      <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="var(--border-card, #E5E7EB)" strokeWidth={strokeWidth} />
      <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={color} strokeWidth={strokeWidth}
        strokeDasharray={circumference} strokeDashoffset={offset}
        strokeLinecap="round" transform={`rotate(-90 ${size / 2} ${size / 2})`}
        style={{ transition: 'stroke-dashoffset 0.6s ease' }} />
      <text x={size / 2} y={size / 2} textAnchor="middle" dominantBaseline="central"
        fill="var(--text-primary, #1A1A2E)" fontSize={size * 0.28} fontWeight={700} fontFamily="DM Sans, sans-serif">
        {score !== null && score !== undefined ? Math.round(score) : '–'}
      </text>
    </svg>
  );
}

export default function ForecastQuality() {
  const { t, locale } = useLanguage();
  const { user } = useAuth();
  const [syncingActuals, setSyncingActuals] = useState(false);
  const [periode, setPeriode] = useState('woche');
  const [refDate, setRefDate] = useState(() => new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Vienna' }));
  const [summaryData, setSummaryData] = useState(null);
  const [dailyData, setDailyData] = useState(null);
  const [config, setConfig] = useState(null);
  const [expandedKunde, setExpandedKunde] = useState(null);
  const [kundeDetail, setKundeDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  // AI Report state
  const [report, setReport] = useState(null);
  const [reportsList, setReportsList] = useState([]);
  const [reportLoading, setReportLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [dailyAnalysis, setDailyAnalysis] = useState(null);

  const loadData = useCallback(() => {
    setLoading(true);
    const datumParam = periode === 'monat' ? refDate.slice(0, 7) : refDate;
    const monatParam = refDate.slice(0, 7);
    Promise.all([
      api(`/api/forecast/quality/summary?periode=${periode}&datum=${datumParam}`),
      api(`/api/forecast/quality/config`),
      api(`/api/forecast/quality/daily?monat=${monatParam}`),
    ]).then(([summary, cfg, daily]) => {
      setSummaryData(summary);
      setConfig(cfg);
      setDailyAnalysis(daily);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [periode, refDate]);

  useEffect(() => { loadData(); }, [loadData]);

  // Load reports list + latest
  const loadReports = useCallback(() => {
    setReportLoading(true);
    Promise.all([
      api('/api/forecast/quality/reports?limit=10'),
      api('/api/forecast/quality/reports/latest'),
    ]).then(([list, latest]) => {
      setReportsList(list.reports || []);
      setReport(latest.report);
    }).catch(() => {}).finally(() => setReportLoading(false));
  }, []);

  useEffect(() => { loadReports(); }, [loadReports]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await fetch('/api/forecast/quality/reports/generate', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: '{}',
      });
      if (res.ok) {
        const data = await res.json();
        setReport({
          ...data.report,
          details: null, // will be loaded on next fetch
        });
        loadReports();
      }
    } catch {}
    setGenerating(false);
  };

  const loadReportById = async (id) => {
    setReportLoading(true);
    try {
      const data = await api(`/api/forecast/quality/reports/latest?id=${id}`);
      setReport(data.report);
    } catch {}
    setReportLoading(false);
  };

  // Load daily data when a customer is expanded
  useEffect(() => {
    if (!expandedKunde || !summaryData?.periode) return;
    const { start, end } = summaryData.periode;
    api(`/api/forecast/quality?kunde_id=${expandedKunde}&start=${start}&end=${end}`)
      .then(d => setKundeDetail(d))
      .catch(() => setKundeDetail(null));
  }, [expandedKunde, summaryData?.periode]);

  // Navigation
  const navigate = (dir) => {
    const d = new Date(refDate + 'T12:00:00');
    if (periode === 'woche') d.setDate(d.getDate() + (dir === 'prev' ? -7 : 7));
    else d.setMonth(d.getMonth() + (dir === 'prev' ? -1 : 1));
    setRefDate(d.toISOString().slice(0, 10));
    setExpandedKunde(null);
  };

  const kunden = summaryData?.kunden || [];
  const gesamt = summaryData?.gesamt || {};
  const periodeInfo = summaryData?.periode || {};
  const cfgData = config?.config || [];

  // Parse thresholds from config array
  const thresholds = useMemo(() => {
    const m = {};
    cfgData.forEach(c => { m[c.key] = typeof c.value === 'string' ? (isNaN(c.value) ? c.value : parseFloat(c.value)) : c.value; });
    return {
      excellent: m.fq_threshold_excellent || 90,
      good: m.fq_threshold_good || 70,
      moderate: m.fq_threshold_moderate || 50,
      show_badges: m.fq_show_grid_badges !== false && m.fq_show_grid_badges !== 'false',
      badge_format: m.fq_badge_format || 'score',
    };
  }, [cfgData]);

  const configObj = useMemo(() => ({ thresholds }), [thresholds]);

  // Period label
  const periodLabel = useMemo(() => {
    if (!periodeInfo.start) return '';
    const fmtShort = (d) => fmtDate(d, locale, { day: '2-digit', month: 'short' });
    if (periode === 'monat') {
      return fmtDate(periodeInfo.start, locale, { month: 'long', year: 'numeric' });
    }
    return `${fmtShort(periodeInfo.start)} – ${fmtShort(periodeInfo.end)}`;
  }, [periodeInfo, periode, locale]);

  const fmt = (n) => n != null ? n.toLocaleString(locale) : '–';

  // Chart data for expanded customer
  const chartData = useMemo(() => {
    if (!kundeDetail?.scores) return [];
    return kundeDetail.scores
      .sort((a, b) => a.datum.localeCompare(b.datum))
      .map(s => ({
        datum: fmtDate(s.datum, locale, { weekday: 'short', day: '2-digit' }),
        score: s.quality_score,
        accuracy: s.accuracy_score,
      }));
  }, [kundeDetail, locale]);

  // Recommendation
  const getRecommendation = (k) => {
    if (k.avg_bias === null) return null;
    if (Math.abs(k.avg_bias) <= 10) return { text: t('forecastQuality.forecastFits'), color: '#10B981' };
    const pct = Math.abs(Math.round(k.avg_bias));
    if (k.avg_bias > 0) return { text: t('forecastQuality.reduceSoll', { pct }), color: '#F59E0B' };
    return { text: t('forecastQuality.reduceSoll', { pct: `-${pct}` }), color: '#338edc' };
  };

  return (
    <div className="p-6 lg:p-page">
      <SharedForecastTabs />
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm mb-4">
        <NavLink to="/" className="text-[var(--text-secondary)] hover:text-content-secondary transition-colors">Home</NavLink>
        <span className="text-[var(--text-tertiary)]">/</span>
        <NavLink to="/forecast" className="text-[var(--text-secondary)] hover:text-content-secondary transition-colors">{t('forecast.title')}</NavLink>
        <span className="text-[var(--text-tertiary)]">/</span>
        <span className="text-forecast">{t('forecastQuality.title')}</span>
      </div>

      {/* Header with period selector */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="xs" onClick={() => navigate('prev')} icon={ChevronLeft} className="text-content-tertiary" />
          <h2 className="text-lg font-bold min-w-[200px] text-center">{periodLabel || '...'}</h2>
          <Button variant="ghost" size="xs" onClick={() => navigate('next')} icon={ChevronRight} className="text-content-tertiary" />
        </div>
        <div className="flex items-center gap-2">
          {user?.rolle === 'administrator' && (
            <button
              onClick={async () => {
                setSyncingActuals(true);
                try {
                  await fetch('/api/customer-forecasts/sync-actuals', { method: 'POST', credentials: 'include' });
                } catch (e) { console.error('Sync actuals error:', e); }
                finally { setSyncingActuals(false); }
              }}
              disabled={syncingActuals}
              className="flex items-center gap-1.5 px-3 py-[6px] rounded-lg text-xs border border-line hover:bg-surface-hover text-content-secondary transition-colors disabled:opacity-50"
              title={t('customerForecast.syncActuals')}
            >
              <RefreshCw size={13} className={syncingActuals ? 'animate-spin' : ''} />
              {t('customerForecast.syncActuals')}
            </button>
          )}
          <div className="inline-flex bg-surface-inset rounded-[10px] p-[3px] gap-[2px] border border-line">
            {[{ key: 'woche', label: t('forecast.view.week') }, { key: 'monat', label: t('forecast.view.month') }].map(v => (
              <button key={v.key} onClick={() => { setPeriode(v.key); setExpandedKunde(null); }}
                className={`px-3 py-[5px] rounded-[7px] text-xs transition-colors ${periode === v.key ? 'bg-surface-card text-content font-semibold shadow-sm' : 'text-content-secondary hover:text-content'}`}>
                {v.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-20 text-[var(--text-secondary)]">Loading...</div>
      ) : (
        <>
          {/* KPI Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {/* Score */}
            <div className="border border-[var(--border-default,#E5E7EB)] rounded-[16px] px-6 py-5">
              <div className="flex items-center gap-4">
                <ScoreRing score={gesamt.avg_quality} size={60} />
                <div>
                  <div className="text-[11px] font-semibold uppercase text-[var(--text-secondary)]" style={{ letterSpacing: '0.8px' }}>{t('forecastQuality.avgScore')}</div>
                  <div className="font-mono text-[28px] font-bold text-[var(--text-primary)] mt-0.5" style={{ letterSpacing: '-0.5px' }}>
                    {gesamt.avg_quality != null ? gesamt.avg_quality.toFixed(1) : '–'}
                  </div>
                </div>
              </div>
            </div>
            {/* Accuracy */}
            <div className="border border-[var(--border-default,#E5E7EB)] rounded-[16px] px-6 py-5">
              <div className="flex items-center gap-2">
                <Crosshair size={16} className="text-[#22D3EE]" />
                <div className="text-[11px] font-semibold uppercase text-[var(--text-secondary)]" style={{ letterSpacing: '0.8px' }}>{t('forecastQuality.avgAccuracy')}</div>
              </div>
              <div className="font-mono text-[28px] font-bold text-[var(--text-primary)] mt-1" style={{ letterSpacing: '-0.5px' }}>
                {gesamt.avg_accuracy != null ? `${gesamt.avg_accuracy.toFixed(1)}%` : '–'}
              </div>
            </div>
            {/* Bias */}
            <div className="border border-[var(--border-default,#E5E7EB)] rounded-[16px] px-6 py-5">
              <div className="flex items-center gap-2">
                <GitCompareArrows size={16} className="text-[#22D3EE]" />
                <div className="text-[11px] font-semibold uppercase text-[var(--text-secondary)]" style={{ letterSpacing: '0.8px' }}>{t('forecastQuality.avgBias')}</div>
              </div>
              <div className="flex items-center gap-2 mt-1">
                <div className="font-mono text-[28px] font-bold text-[var(--text-primary)]" style={{ letterSpacing: '-0.5px' }}>
                  {gesamt.avg_bias != null ? `${gesamt.avg_bias > 0 ? '+' : ''}${gesamt.avg_bias.toFixed(1)}%` : '–'}
                </div>
                {gesamt.avg_bias != null && (
                  <span className="text-[11px] font-medium px-2 py-0.5 rounded-full" style={{
                    color: biasColor(gesamt.avg_bias),
                    backgroundColor: `${biasColor(gesamt.avg_bias)}15`,
                  }}>
                    {gesamt.avg_bias > 10 ? t('forecastQuality.tooOptimistic') : gesamt.avg_bias < -10 ? t('forecastQuality.tooPessimistic') : t('forecastQuality.good')}
                  </span>
                )}
              </div>
            </div>
            {/* Consistency */}
            <div className="border border-[var(--border-default,#E5E7EB)] rounded-[16px] px-6 py-5">
              <div className="flex items-center gap-2">
                <Gauge size={16} className="text-[#22D3EE]" />
                <div className="text-[11px] font-semibold uppercase text-[var(--text-secondary)]" style={{ letterSpacing: '0.8px' }}>{t('forecastQuality.avgConsistency')}</div>
              </div>
              <div className="font-mono text-[28px] font-bold text-[var(--text-primary)] mt-1" style={{ letterSpacing: '-0.5px' }}>
                {(() => {
                  const withC = kunden.filter(k => k.consistency != null);
                  if (!withC.length) return '–';
                  return `${(withC.reduce((s, k) => s + k.consistency, 0) / withC.length).toFixed(1)}%`;
                })()}
              </div>
            </div>
          </div>

          {/* AI Weekly Report Card */}
          {(report || reportsList.length > 0) && (
            <div className="border border-[var(--border-default,#E5E7EB)] rounded-[16px] mb-6">
              <div className="px-5 py-3 border-b border-[var(--border-table,#F3F4F6)] flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <Target className="w-4 h-4 text-content" />
                  <h3 className="text-sm font-semibold text-content">
                    {t('forecastQuality.weeklyReport')}
                    {report?.kw ? ` KW ${report.kw}` : ''}
                    {report?.start_datum && report?.end_datum ? (
                      <span className="font-normal text-[var(--text-secondary)] ml-1">
                        ({fmtDate(report.start_datum, locale, { day: '2-digit', month: '2-digit' })}–{fmtDate(report.end_datum, locale, { day: '2-digit', month: 'short', year: 'numeric' })})
                      </span>
                    ) : null}
                  </h3>
                </div>
                <div className="flex items-center gap-2">
                  {/* Report dropdown */}
                  {reportsList.length > 1 && (
                    <select
                      value={report?.id || ''}
                      onChange={(e) => loadReportById(e.target.value)}
                      className="px-2 py-1 text-xs rounded-lg border border-line bg-surface-elevated text-content focus:outline-none"
                    >
                      {reportsList.map(r => (
                        <option key={r.id} value={r.id}>
                          {fmtDate(r.start_datum, locale, { day: '2-digit', month: '2-digit' })}–{fmtDate(r.end_datum, locale, { day: '2-digit', month: '2-digit' })} (Score: {r.gesamt_score != null ? Number(r.gesamt_score).toFixed(0) : '–'})
                        </option>
                      ))}
                    </select>
                  )}
                  <button
                    onClick={handleGenerate}
                    disabled={generating}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-line bg-surface-elevated text-[var(--text-secondary)] hover:text-content transition-colors disabled:opacity-50"
                  >
                    <RefreshCw className={`w-3 h-3 ${generating ? 'animate-spin' : ''}`} />
                    {t('forecastQuality.generateNow')}
                  </button>
                </div>
              </div>
              <div className="px-5 py-4">
                {report?.gesamt_score != null && (
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-sm font-semibold text-[var(--text-primary)]">
                      {t('forecastQuality.qualityScore')}: {Number(report.gesamt_score).toFixed(0)}/100
                    </span>
                    <span className="text-xs px-2 py-0.5 rounded-full font-medium"
                      style={{ color: scoreColor(Number(report.gesamt_score), configObj), backgroundColor: `${scoreColor(Number(report.gesamt_score), configObj)}15` }}>
                      {scoreLabel(Number(report.gesamt_score), configObj, t)}
                    </span>
                  </div>
                )}
                {report?.ai_summary ? (
                  <div className="text-sm leading-relaxed"
                    style={{ maxHeight: 400, overflowY: 'auto' }}>
                    <style>{`
                      .fq-md-h2 { font-size: 16px; font-weight: 600; margin: 20px 0 8px; color: var(--text-primary); }
                      .fq-md-h3 { font-size: 15px; font-weight: 600; margin: 16px 0 6px; color: var(--text-primary); }
                      .fq-md-h4 { font-size: 14px; font-weight: 600; margin: 12px 0 4px; color: var(--text-primary); }
                      .fq-md-li { margin-left: 16px; margin-bottom: 4px; color: var(--text-secondary); }
                      .fq-md-li::before { content: "•"; margin-right: 8px; color: var(--text-tertiary); }
                    `}</style>
                    <div
                      className="text-[var(--text-secondary)] [&_strong]:text-[var(--text-primary)] [&_strong]:font-semibold"
                      dangerouslySetInnerHTML={{ __html: renderMarkdown(report.ai_summary) }}
                    />
                  </div>
                ) : reportLoading ? (
                  <div className="text-center py-4 text-[var(--text-secondary)] text-sm">Loading...</div>
                ) : (
                  <div className="text-center py-4 text-[var(--text-tertiary)] text-sm">
                    {t('forecastQuality.generateNow')}
                  </div>
                )}
                {report?.generated_at && (
                  <div className="mt-3 text-[11px] text-[var(--text-tertiary)]">
                    {t('forecastQuality.generatedAt')}: {new Date(report.generated_at).toLocaleString(locale === 'de' ? 'de-DE' : 'en-US')}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Tages-Analyse */}
          {dailyAnalysis?.days?.length > 0 && (() => {
            const days = dailyAnalysis.days;
            const summary = dailyAnalysis.summary || {};
            const chartDays = days.map(d => ({
              datum: fmtDate(d.datum, locale, { day: '2-digit', month: '2-digit' }),
              soll: d.soll,
              [t('forecastQuality.verfuegbar')]: d.verfuegbar,
              SOLL: d.soll,
            }));
            const statusBadge = (acc) => {
              if (acc == null) return null;
              if (acc >= 90) return <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-green-600">✓ {t('forecastQuality.statusGood')}</span>;
              if (acc >= 70) return <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-500">⚠ {t('forecastQuality.statusModerate')}</span>;
              return <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-red-500">✗ {t('forecastQuality.statusBad')}</span>;
            };
            return (
              <div className="border border-[var(--border-default,#E5E7EB)] rounded-[16px] mb-6 overflow-hidden">
                <div className="px-5 py-3 border-b border-[var(--border-table,#F3F4F6)] flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-content" />
                    <h3 className="text-sm font-semibold text-content">{t('forecastQuality.dailyAnalysis')}</h3>
                  </div>
                  <div className="flex items-center gap-4 text-[11px]">
                    <span className="text-[var(--text-secondary)]">{t('forecastQuality.avgAccuracy')}: <strong className="text-[var(--text-primary)] font-mono">{summary.avg_accuracy != null ? `${summary.avg_accuracy}%` : '–'}</strong></span>
                    <span className="text-[var(--text-secondary)]">{t('forecastQuality.avgBias')}: <strong className="font-mono" style={{ color: biasColor(summary.avg_bias) }}>{summary.avg_bias != null ? `${summary.avg_bias > 0 ? '+' : ''}${summary.avg_bias}%` : '–'}</strong>
                      {summary.avg_bias != null && <span className="ml-1 text-[10px]">({summary.avg_bias > 0 ? t('forecastQuality.tooOptimistic') : t('forecastQuality.tooPessimistic')})</span>}
                    </span>
                    <span className="text-[var(--text-secondary)]">{t('forecastQuality.daysAbove90')}: <strong className="text-[var(--text-primary)] font-mono">{summary.days_above_90 ?? '–'}/{summary.days_count ?? '–'}</strong></span>
                  </div>
                </div>

                {/* Chart */}
                <div className="px-5 py-4">
                  <div className="h-[200px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={chartDays} margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F3F4F6" />
                        <XAxis dataKey="datum" tick={{ fontSize: 10, fill: '#9CA3AF' }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fontSize: 10, fill: '#9CA3AF' }} axisLine={false} tickLine={false} width={40} tickFormatter={v => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v} />
                        <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #E5E7EB', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', backgroundColor: 'var(--bg-card, #fff)' }} />
                        <Bar dataKey="SOLL" fill="#E5E7EB" radius={[4, 4, 0, 0]} name={t('forecastQuality.soll')} />
                        <Bar dataKey={t('forecastQuality.verfuegbar')} fill="#338edc" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="flex items-center gap-4 mt-1 ml-1">
                    <div className="flex items-center gap-1"><div className="w-3 h-2 bg-[#E5E7EB] rounded-sm" /><span className="text-[9px] text-[var(--text-secondary)]">{t('forecastQuality.soll')}</span></div>
                    <div className="flex items-center gap-1"><div className="w-3 h-2 bg-[#338edc] rounded-sm" /><span className="text-[9px] text-[var(--text-secondary)]">{t('forecastQuality.verfuegbar')}</span></div>
                  </div>
                </div>

                {/* Table */}
                <div className="border-t border-[var(--border-table,#F3F4F6)]">
                  <div className="max-h-[280px] overflow-y-auto">
                    <table className="w-full text-[12px]">
                      <thead className="sticky top-0 bg-surface-card">
                        <tr className="text-[10px] uppercase text-[var(--text-tertiary)]" style={{ letterSpacing: '0.5px' }}>
                          <th className="text-left px-4 py-2 font-semibold">{t('forecastQuality.day')}</th>
                          <th className="text-right px-3 py-2 font-semibold">{t('forecastQuality.soll')}</th>
                          <th className="text-right px-3 py-2 font-semibold">{t('forecastQuality.verfuegbar')}</th>
                          <th className="text-right px-3 py-2 font-semibold">{t('forecastQuality.diff')}</th>
                          <th className="text-right px-3 py-2 font-semibold">{t('forecastQuality.accuracy')}</th>
                          <th className="text-center px-3 py-2 font-semibold">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {days.map(d => (
                          <tr key={d.datum} style={{ borderBottom: '1px solid var(--border-subtle, #F3F4F6)' }}>
                            <td className="px-4 py-1.5 text-[var(--text-primary)] font-medium">{fmtDate(d.datum, locale, { weekday: 'short', day: '2-digit', month: '2-digit' })}</td>
                            <td className="text-right px-3 py-1.5 font-mono text-[var(--text-primary)]">{d.soll.toLocaleString(locale)}</td>
                            <td className="text-right px-3 py-1.5 font-mono text-[var(--text-primary)]">{d.verfuegbar.toLocaleString(locale)}</td>
                            <td className="text-right px-3 py-1.5 font-mono" style={{ color: d.diff >= 0 ? '#10B981' : '#EF4444' }}>{d.diff > 0 ? '+' : ''}{d.diff.toLocaleString(locale)}</td>
                            <td className="text-right px-3 py-1.5 font-mono" style={{ color: scoreColor(d.accuracy, { thresholds: { excellent: 90, good: 70, moderate: 50 } }) }}>{d.accuracy != null ? `${d.accuracy}%` : '–'}</td>
                            <td className="text-center px-3 py-1.5">{statusBadge(d.accuracy)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Customer Ranking Table */}
          {(() => {
            const gridCols = '1fr 80px 90px 90px 90px 70px 50px 36px';
            const gridStyle = { display: 'grid', gridTemplateColumns: gridCols, columnGap: 16 };
            const hStyle = { fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-tertiary)' };
            return (
          <div className="border border-[var(--border-default,#E5E7EB)] rounded-[16px] overflow-hidden">
            {/* Header */}
            <div style={{ ...gridStyle, borderBottom: '1px solid #F3F4F6' }}>
              <div className="text-left px-4 py-3" style={hStyle}>{t('forecast.grid.customer_col')}</div>
              <div className="text-center px-3 py-3" style={hStyle}>{t('forecastQuality.qualityScore')}</div>
              <div className="text-center px-3 py-3" style={hStyle}>{t('forecastQuality.accuracy')}</div>
              <div className="text-center px-3 py-3" style={hStyle}>{t('forecastQuality.bias')}</div>
              <div className="text-center px-3 py-3" style={hStyle}>{t('forecastQuality.consistency')}</div>
              <div className="text-center px-3 py-3" style={hStyle}>{t('forecastQuality.trend')}</div>
              <div />
              <div />
            </div>
            {/* Rows */}
            {kunden.length === 0 && (
              <div className="text-center py-12 text-[var(--text-secondary)]">{t('common.noData')}</div>
            )}
            {kunden.map(k => {
              const isExpanded = expandedKunde === k.kunde_id;
              const color = scoreColor(k.quality_score, configObj);
              const rec = getRecommendation(k);
              return (
                <div key={k.kunde_id}>
                  <div
                    className="hover:bg-surface-hover transition-colors cursor-pointer items-center"
                    style={{ ...gridStyle, borderBottom: '1px solid var(--border-subtle, #F3F4F6)' }}
                    onClick={() => { setExpandedKunde(isExpanded ? null : k.kunde_id); setKundeDetail(null); }}
                  >
                    <div className="px-4 py-3">
                      <span className="text-[13px] font-medium text-[var(--text-primary)]">{k.kunde_name}</span>
                      <div className="text-[11px] text-[var(--text-tertiary)] mt-0.5">{k.tage} {t('common.days')}</div>
                    </div>
                    <div className="text-center px-3 py-3">
                      <span style={{ background: `${color}15`, color, padding: '3px 8px', borderRadius: 6, fontSize: 12, fontWeight: 600, fontFamily: 'monospace' }}>
                        {k.quality_score != null ? Math.round(k.quality_score) : '–'}
                      </span>
                    </div>
                    <div className="text-center px-3 py-3 font-mono text-[13px] text-[var(--text-primary)]">
                      {k.avg_accuracy != null ? `${k.avg_accuracy.toFixed(1)}%` : '–'}
                    </div>
                    <div className="text-center px-3 py-3">
                      {k.avg_bias != null ? (
                        <span className="font-mono text-[13px]" style={{ color: biasColor(k.avg_bias) }}>
                          {k.avg_bias > 0 ? '+' : ''}{k.avg_bias.toFixed(1)}%
                        </span>
                      ) : '–'}
                    </div>
                    <div className="text-center px-3 py-3 font-mono text-[13px] text-[var(--text-primary)]">
                      {k.consistency != null ? `${k.consistency.toFixed(1)}%` : '–'}
                    </div>
                    <div className="text-center px-3 py-3">
                      <Minus className="w-3.5 h-3.5 mx-auto text-[var(--text-tertiary)]" />
                    </div>
                    <div />
                    <div className="flex items-center justify-center py-3">
                      {isExpanded ? <ChevronUp className="w-4 h-4 text-[var(--text-tertiary)]" /> : <ChevronDown className="w-4 h-4 text-[var(--text-tertiary)]" />}
                    </div>
                  </div>
                  {/* Expanded detail */}
                  {isExpanded && (
                    <div className="px-4 py-4" style={{ backgroundColor: 'var(--bg-inset, #F8F9FC)' }}>
                            {!kundeDetail ? (
                              <div className="text-center py-4 text-[var(--text-secondary)] text-sm">Loading...</div>
                            ) : (
                              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                {/* Chart */}
                                <div className="border border-[var(--border-default,#E5E7EB)] rounded-[12px] bg-white p-4" style={{ backgroundColor: 'var(--bg-card, #fff)' }}>
                                  <div className="text-[11px] font-semibold uppercase text-[var(--text-secondary)] mb-3" style={{ letterSpacing: '0.8px' }}>Score-Verlauf</div>
                                  {chartData.length > 0 ? (
                                    <ResponsiveContainer width="100%" height={160}>
                                      <AreaChart data={chartData}>
                                        <defs>
                                          <linearGradient id="scoreGrad" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="0%" stopColor="#338edc" stopOpacity={0.15} />
                                            <stop offset="100%" stopColor="#338edc" stopOpacity={0} />
                                          </linearGradient>
                                        </defs>
                                        <CartesianGrid strokeDasharray="3 3" stroke="var(--border-table, #F3F4F6)" />
                                        <XAxis dataKey="datum" tick={{ fontSize: 10, fill: 'var(--text-tertiary)' }} axisLine={false} tickLine={false} />
                                        <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: 'var(--text-tertiary)' }} axisLine={false} tickLine={false} width={30} />
                                        <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #E5E7EB', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }} />
                                        <Area type="monotone" dataKey="score" stroke="#338edc" strokeWidth={2} fill="url(#scoreGrad)" name="Score" />
                                        <Area type="monotone" dataKey="accuracy" stroke="#22D3EE" strokeWidth={1.5} fill="none" name="Accuracy" strokeDasharray="4 4" />
                                      </AreaChart>
                                    </ResponsiveContainer>
                                  ) : (
                                    <div className="text-center py-8 text-[var(--text-tertiary)] text-sm">{t('common.noData')}</div>
                                  )}
                                </div>
                                {/* Daily table + recommendation */}
                                <div className="border border-[var(--border-default,#E5E7EB)] rounded-[12px] bg-white overflow-hidden" style={{ backgroundColor: 'var(--bg-card, #fff)' }}>
                                  <div className="px-4 pt-3 pb-2 border-b border-[var(--border-table,#F3F4F6)]">
                                    <div className="text-[11px] font-semibold uppercase text-[var(--text-secondary)]" style={{ letterSpacing: '0.8px' }}>{t('forecastQuality.detail')}</div>
                                  </div>
                                  <div className="max-h-[180px] overflow-y-auto">
                                    <table className="w-full text-[12px]">
                                      <thead>
                                        <tr className="text-[10px] uppercase text-[var(--text-tertiary)]" style={{ letterSpacing: '0.5px' }}>
                                          <th className="text-left px-3 py-1.5 font-semibold">Datum</th>
                                          <th className="text-right px-2 py-1.5 font-semibold">SOLL</th>
                                          <th className="text-right px-2 py-1.5 font-semibold">{t('forecastQuality.available')}</th>
                                          <th className="text-right px-2 py-1.5 font-semibold">IST</th>
                                          <th className="text-right px-2 py-1.5 font-semibold">Acc</th>
                                          <th className="text-right px-2 py-1.5 font-semibold">Bias</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {(kundeDetail.scores || []).sort((a, b) => a.datum.localeCompare(b.datum)).map(s => (
                                          <tr key={s.datum} style={{ borderBottom: '1px solid var(--border-subtle, #F3F4F6)' }}>
                                            <td className="px-3 py-1.5 text-[var(--text-primary)]">{fmtDate(s.datum, locale, { weekday: 'short', day: '2-digit', month: '2-digit' })}</td>
                                            <td className="text-right px-2 py-1.5 font-mono text-[var(--text-primary)]">{fmt(s.soll)}</td>
                                            <td className="text-right px-2 py-1.5 font-mono text-[var(--text-primary)]">{fmt(s.verfuegbar)}</td>
                                            <td className="text-right px-2 py-1.5 font-mono text-[var(--text-primary)]">{s.ist != null ? fmt(s.ist) : '–'}</td>
                                            <td className="text-right px-2 py-1.5 font-mono" style={{ color: scoreColor(s.accuracy_score, configObj) }}>{s.accuracy_score != null ? `${s.accuracy_score.toFixed(0)}%` : '–'}</td>
                                            <td className="text-right px-2 py-1.5 font-mono" style={{ color: biasColor(s.bias_pct) }}>{s.bias_pct != null ? `${s.bias_pct > 0 ? '+' : ''}${s.bias_pct.toFixed(0)}%` : '–'}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                  {rec && (
                                    <div className="px-4 py-2.5 border-t border-[var(--border-table,#F3F4F6)] flex items-center gap-2">
                                      <span className="text-[10px] font-semibold uppercase text-[var(--text-tertiary)]" style={{ letterSpacing: '0.5px' }}>{t('forecastQuality.recommendation')}:</span>
                                      <span className="text-[12px] font-medium" style={{ color: rec.color }}>{rec.text}</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
            );
          })()}
        </>
      )}
    </div>
  );
}
